import {
  FormControlLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
  Box,
  Chip,
  Autocomplete,
  ToggleButton,
  ToggleButtonGroup
} from "@mui/material";

import { FieldMetadata } from "~/config/clientConfigFieldMetadata";

interface ConfigFieldRendererProps {
  fieldKey: string;
  metadata: FieldMetadata;
  value: unknown;
  onChange: (key: string, value: unknown) => void;
}

export function ConfigFieldRenderer({
  fieldKey,
  metadata,
  value,
  onChange
}: ConfigFieldRendererProps) {
  if (metadata.hidden) {
    return null;
  }

  const renderField = () => {
    switch (metadata.type) {
      case "boolean":
        return (
          <FormControlLabel
            control={
              <Switch
                checked={Boolean(value)}
                onChange={(e) => onChange(fieldKey, e.target.checked)}
              />
            }
            label={
              <Box>
                <Typography variant="body2">{metadata.label}</Typography>
                {metadata.description && (
                  <Typography variant="caption" color="text.secondary">
                    {metadata.description}
                  </Typography>
                )}
              </Box>
            }
          />
        );

      case "select":
        return (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              {metadata.label}
            </Typography>
            {metadata.description && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mb: 1, display: "block" }}
              >
                {metadata.description}
              </Typography>
            )}
            <Select
              value={String(value)}
              onChange={(e) => onChange(fieldKey, e.target.value)}
              fullWidth
              size="small"
            >
              {metadata.options?.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </Box>
        );

      case "text": {
        const textValue =
          typeof value === "string" || typeof value === "number"
            ? String(value)
            : "";
        return (
          <TextField
            label={metadata.label}
            value={textValue}
            onChange={(e) => onChange(fieldKey, e.target.value)}
            fullWidth
            size="small"
            helperText={metadata.description}
            sx={{ mb: 2 }}
          />
        );
      }

      case "chips":
        // Special handling for inv_inventoryDateLabel - use button chips
        if (fieldKey === "inv_inventoryDateLabel") {
          return (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                {metadata.label}
              </Typography>
              {metadata.description && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mb: 1, display: "block" }}
                >
                  {metadata.description}
                </Typography>
              )}
              <ToggleButtonGroup
                value={String(value)}
                exclusive
                onChange={(_, newValue) => {
                  if (newValue !== null) {
                    onChange(fieldKey, newValue);
                  }
                }}
                size="small"
              >
                {metadata.options?.map((option) => (
                  <ToggleButton key={option} value={option}>
                    <Chip
                      label={option}
                      size="small"
                      variant={String(value) === option ? "filled" : "outlined"}
                    />
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>
          );
        }

        // Default chips handling with non-clearable for site_defaultLanguageCode and ap_totesPlacement
        return (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              {metadata.label}
            </Typography>
            {metadata.description && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mb: 1, display: "block" }}
              >
                {metadata.description}
              </Typography>
            )}
            <Autocomplete
              options={metadata.options || []}
              value={String(value)}
              onChange={(_, newValue) => {
                // Don't allow clearing for site_defaultLanguageCode and ap_totesPlacement
                if (
                  (fieldKey === "site_defaultLanguageCode" ||
                    fieldKey === "ap_totesPlacement") &&
                  !newValue
                ) {
                  return;
                }
                onChange(fieldKey, newValue || "");
              }}
              disableClearable={
                fieldKey === "site_defaultLanguageCode" ||
                fieldKey === "ap_totesPlacement"
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  sx={{
                    minWidth: 120,
                    maxWidth: 200
                  }}
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props} key={option}>
                  <Chip label={option} size="small" />
                </Box>
              )}
              size="small"
              sx={{
                width: "fit-content",
                maxWidth: 200
              }}
            />
          </Box>
        );

      case "multiChips":
        // Special handling for fc_autostore_homepages - use toggle buttons
        if (fieldKey === "fc_autostore_homepages") {
          const currentValues = Array.isArray(value) ? (value as string[]) : [];

          return (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                {metadata.label}
              </Typography>
              {metadata.description && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mb: 1, display: "block" }}
                >
                  {metadata.description}
                </Typography>
              )}
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {metadata.options?.map((option) => {
                  const isSelected = currentValues.includes(option);

                  return (
                    <ToggleButton
                      key={option}
                      value={option}
                      selected={isSelected}
                      onChange={() => {
                        const newValues = isSelected
                          ? currentValues.filter((v) => v !== option)
                          : [...currentValues, option];
                        onChange(fieldKey, newValues);
                      }}
                      size="small"
                    >
                      <Chip
                        label={option}
                        size="small"
                        variant={isSelected ? "filled" : "outlined"}
                      />
                    </ToggleButton>
                  );
                })}
              </Box>
            </Box>
          );
        }

        // Default multiChips handling with autocomplete
        return (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              {metadata.label}
            </Typography>
            {metadata.description && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mb: 1, display: "block" }}
              >
                {metadata.description}
              </Typography>
            )}
            <Autocomplete
              multiple
              options={metadata.options || []}
              value={Array.isArray(value) ? (value as string[]) : []}
              onChange={(_, newValue) => onChange(fieldKey, newValue)}
              renderInput={(params) => (
                <TextField {...params} size="small" fullWidth />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={option}
                    {...getTagProps({ index })}
                    key={option}
                    size="small"
                  />
                ))
              }
              renderOption={(props, option) => (
                <Box component="li" {...props} key={option}>
                  <Chip label={option} size="small" />
                </Box>
              )}
              size="small"
            />
          </Box>
        );

      case "keyValue":
        // Special handling for inv_uomOptions - use label/value inputs
        if (fieldKey === "inv_uomOptions") {
          const currentOptions = Array.isArray(value)
            ? (value as { value: string; label: string }[])
            : [];

          return (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                {metadata.label}
              </Typography>
              {metadata.description && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mb: 1, display: "block" }}
                >
                  {metadata.description}
                </Typography>
              )}
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {currentOptions.map((option, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      gap: 1,
                      alignItems: "center",
                      p: 2,
                      border: 1,
                      borderColor: "divider",
                      borderRadius: 1
                    }}
                  >
                    <TextField
                      label="Value"
                      value={option.value}
                      onChange={(e) => {
                        const newOptions = [...currentOptions];
                        newOptions[index] = {
                          ...option,
                          value: e.target.value
                        };
                        onChange(fieldKey, newOptions);
                      }}
                      size="small"
                      sx={{ minWidth: 120 }}
                    />
                    <TextField
                      label="Label"
                      value={option.label}
                      onChange={(e) => {
                        const newOptions = [...currentOptions];
                        newOptions[index] = {
                          ...option,
                          label: e.target.value
                        };
                        onChange(fieldKey, newOptions);
                      }}
                      size="small"
                      sx={{ minWidth: 120 }}
                    />
                    <Box
                      component="button"
                      onClick={() => {
                        const newOptions = currentOptions.filter(
                          (_, i) => i !== index
                        );
                        onChange(fieldKey, newOptions);
                      }}
                      sx={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        color: "error.main",
                        fontSize: "18px",
                        padding: "4px",
                        borderRadius: "4px",
                        "&:hover": {
                          backgroundColor: "error.light",
                          color: "white"
                        }
                      }}
                      aria-label={`Remove option ${index + 1}`}
                    >
                      ×
                    </Box>
                  </Box>
                ))}
                <Box
                  component="button"
                  onClick={() => {
                    const newOptions = [
                      ...currentOptions,
                      { value: "", label: "" }
                    ];
                    onChange(fieldKey, newOptions);
                  }}
                  sx={{
                    background: "none",
                    border: "1px dashed",
                    borderColor: "primary.main",
                    cursor: "pointer",
                    color: "primary.main",
                    padding: "8px 16px",
                    borderRadius: "4px",
                    "&:hover": {
                      backgroundColor: "primary.light",
                      color: "white"
                    }
                  }}
                >
                  + Add UOM Option
                </Box>
              </Box>
            </Box>
          );
        }

        // Default keyValue handling with JSON editor
        return (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              {metadata.label}
            </Typography>
            {metadata.description && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mb: 1, display: "block" }}
              >
                {metadata.description}
              </Typography>
            )}
            <TextField
              value={
                typeof value === "object" && value !== null
                  ? JSON.stringify(value, null, 2)
                  : ""
              }
              onChange={(e) => {
                try {
                  const parsedValue = JSON.parse(e.target.value) as unknown;
                  onChange(fieldKey, parsedValue);
                } catch {
                  // If parsing fails, store as string for now
                  onChange(fieldKey, e.target.value);
                }
              }}
              fullWidth
              multiline
              rows={4}
              size="small"
              helperText="Enter valid JSON (key-value pairs)"
            />
          </Box>
        );

      case "array":
      case "object":
        // These are handled in JSON editor
        return null;

      default:
        return null;
    }
  };

  return renderField();
}
